import SEO from '../Components/SEO';
import React from "react";
import {
  Eye,
  Settings,
  MessageSquare,
  Cookie,
  ExternalLink,
  Lock,
  Share2,
  Mail,
  Clock,
  Shield,
  User,
  Globe,
  Edit3,
  Phone
} from "lucide-react";

const PrivacyPolicy = () => {
  const brandColor = "#fc7303";

  const sections = [
    { id: "collect", label: "Information We Collect", icon: <Eye size={20} color={brandColor} /> },
    { id: "use", label: "How We Use Your Information", icon: <Settings size={20} color={brandColor} /> },
    { id: "contact-info", label: "Enquiry & Contact Info", icon: <MessageSquare size={20} color={brandColor} /> },
    { id: "cookies", label: "Cookies", icon: <Cookie size={20} color={brandColor} /> },
    { id: "third-party", label: "Third-Party Services", icon: <ExternalLink size={20} color={brandColor} /> },
    { id: "protect", label: "How We Protect Data", icon: <Lock size={20} color={brandColor} /> },
    { id: "sharing", label: "Sharing of Information", icon: <Share2 size={20} color={brandColor} /> },
    { id: "marketing", label: "Marketing Communications", icon: <Mail size={20} color={brandColor} /> },
    { id: "retention", label: "Data Retention", icon: <Clock size={20} color={brandColor} /> },
    { id: "rights", label: "Your Privacy Rights", icon: <Shield size={20} color={brandColor} /> },
    { id: "children", label: "Children's Privacy", icon: <User size={20} color={brandColor} /> },
    { id: "external", label: "External Websites", icon: <Globe size={20} color={brandColor} /> },
    { id: "changes", label: "Changes to This Policy", icon: <Edit3 size={20} color={brandColor} /> },
    { id: "contact", label: "Contact Us", icon: <Phone size={20} color={brandColor} /> },
  ];

  return (
    <div
      style={{
        maxWidth: "1200px",
      }}>
      <SEO
        title="Privacy Policy | Sree Raghavendra Doors & Hardware"
        description="Privacy policy and data protection terms for Sree Raghavendra Doors, Plywood & Hardware website visitors and customers in Rajamahendravaram."
        canonicalUrl="/privacy-policy"
      />

        margin: "0 auto",
        padding: "60px 24px",
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      <style>{`
        .policy-layout {
          display: flex;
          gap: 40px;
          align-items: flex-start;
        }
        
        .policy-sidebar {
          width: 300px;
          position: sticky;
          top: 100px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 24px;
          max-height: calc(100vh - 140px);
          overflow-y: auto;
          box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.05);
        }
        
        .policy-sidebar-title {
          font-family: 'Oswald', sans-serif;
          font-size: 0.95rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #64748b;
          margin-bottom: 16px;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 8px;
        }
        
        .policy-sidebar-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .policy-sidebar-link {
          color: #475569;
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .policy-sidebar-link:hover {
          color: ${brandColor};
          transform: translateX(4px);
        }
        
        .policy-content {
          flex: 1;
          color: #334155;
          line-height: 1.8;
        }
        
        .policy-section {
          scroll-margin-top: 110px;
          margin-bottom: 40px;
        }

        .policy-section h3 {
          font-family: 'Oswald', sans-serif;
          color: #0f172a;
          font-size: 1.4rem;
          margin-bottom: 15px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid #f1f5f9;
          padding-bottom: 8px;
        }
        
        @media (max-width: 991px) {
          .policy-layout {
            flex-direction: column;
            gap: 20px;
          }
          
          .policy-sidebar {
            width: 100%;
            position: relative;
            top: 0;
            max-height: none;
            margin-bottom: 20px;
            padding: 20px;
          }
        }
      `}</style>

      <div className="policy-layout">
        {/* SIDEBAR TABLE OF CONTENTS */}
        <aside className="policy-sidebar">
          <div className="policy-sidebar-title">On This Page</div>
          <ul className="policy-sidebar-list">
            {sections.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`} className="policy-sidebar-link">
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        {/* POLICY CONTENT */}
        <main className="policy-content">
          <h1
            style={{
              fontFamily: "'Oswald', sans-serif",
              color: brandColor,
              marginBottom: "10px",
              fontWeight: "700",
              fontSize: "2.5rem",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Privacy Policy
          </h1>
          <p style={{ color: "#64748b", fontSize: "0.95rem", marginBottom: "40px" }}>
            Last Updated: August 2026
          </p>

          <p style={{ fontSize: "1.1rem", marginBottom: "45px", color: "#1e293b" }}>
            At <strong>Sree Raghavendra Hardware, Plywoods & Doors</strong>, we respect your privacy and are committed to protecting the information you share with us. This Privacy Policy explains how we may collect, use, and protect information when you visit or interact with our website.
          </p>

          <div className="policy-section" id="collect">
            <h3>{sections[0].icon} Information We Collect</h3>
            <p>
              We may collect information that you voluntarily provide when you contact us or submit an enquiry, including:
            </p>
            <ul style={{ paddingLeft: "20px", marginBottom: "20px" }}>
              <li>Name</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Location or address</li>
              <li>Product requirements</li>
              <li>Enquiry details</li>
              <li>Any other information you choose to provide</li>
            </ul>
            <p>
              We may also automatically receive limited technical information when you visit our website, such as browser type, device information, IP address, pages visited, and general website usage information.
            </p>
          </div>

          <div className="policy-section" id="use">
            <h3>{sections[1].icon} How We Use Your Information</h3>
            <p>
              We may use the information collected through our website to:
            </p>
            <ul style={{ paddingLeft: "20px", marginBottom: "20px" }}>
              <li>Respond to your enquiries.</li>
              <li>Provide product information.</li>
              <li>Understand your product requirements.</li>
              <li>Provide quotations or other business information when requested.</li>
              <li>Contact you regarding your enquiry.</li>
              <li>Provide customer support.</li>
              <li>Improve our website and services.</li>
              <li>Maintain website security.</li>
              <li>Comply with applicable legal requirements.</li>
            </ul>
          </div>

          <div className="policy-section" id="contact-info">
            <h3>{sections[2].icon} Enquiry & Contact Information</h3>
            <p>
              If you submit your information through our website contact form or enquiry form, we may use the information to contact you regarding your request. For example, if you enquire about doors, plywood, hardware, PVC doors, wooden doors, or related products, our team may contact you using the phone number, email address, or other contact details you have provided.
            </p>
          </div>

          <div className="policy-section" id="cookies">
            <h3>{sections[3].icon} Cookies</h3>
            <p>
              Our website may use cookies or similar technologies to improve website functionality and understand how visitors use our website. Cookies may help us understand website traffic, improve performance, remember certain preferences, and enhance the overall user experience. You can manage or disable cookies through your browser settings. Disabling certain cookies may affect some website functionality.
            </p>
          </div>

          <div className="policy-section" id="third-party">
            <h3>{sections[4].icon} Third-Party Services</h3>
            <p>
              Our website may use third-party services such as website hosting, analytics, communication tools, maps, social media integrations, or other website-related services. These third parties may process certain information according to their own privacy policies. We recommend reviewing the privacy policies of third-party services where applicable.
            </p>
          </div>

          <div className="policy-section" id="protect">
            <h3>{sections[5].icon} How We Protect Your Information</h3>
            <p>
              We take reasonable measures to protect the information provided to us from unauthorized access, misuse, loss, alteration, or disclosure. However, no online method of storing or transmitting information can be guaranteed to be completely secure.
            </p>
          </div>

          <div className="policy-section" id="sharing">
            <h3>{sections[6].icon} Sharing of Information</h3>
            <p>
              We do not sell or rent your personal information to third parties. We may share information where reasonably necessary with service providers, technology providers, delivery or business partners, professional advisors, or government authorities where required by applicable law. Any information shared will be limited to what is reasonably necessary for the relevant purpose.
            </p>
          </div>

          <div className="policy-section" id="marketing">
            <h3>{sections[7].icon} Marketing Communications</h3>
            <p>
              If you provide your contact information for an enquiry, our team may contact you regarding your enquiry, products, services, offers, or other relevant business information. You may request us to stop sending promotional communications at any time by contacting us.
            </p>
          </div>

          <div className="policy-section" id="retention">
            <h3>{sections[8].icon} Data Retention</h3>
            <p>
              We retain personal information only for as long as reasonably necessary for business, customer service, legal, accounting, or regulatory purposes. When information is no longer required, we may securely delete or anonymize it, subject to applicable legal requirements.
            </p>
          </div>

          <div className="policy-section" id="rights">
            <h3>{sections[9].icon} Your Privacy Rights</h3>
            <p>
              Depending on applicable law, you may have the right to request information about the personal data we hold about you and request correction or deletion of information where applicable. You may also contact us if you have concerns regarding the use of your personal information.
            </p>
          </div>

          <div className="policy-section" id="children">
            <h3>{sections[10].icon} Children's Privacy</h3>
            <p>
              Our website is intended for a general audience. We do not knowingly collect personal information from children for purposes that are not permitted under applicable law.
            </p>
          </div>

          <div className="policy-section" id="external">
            <h3>{sections[11].icon} External Websites</h3>
            <p>
              Our website may contain links to third-party websites, manufacturers, brands, social media platforms, or other external services. We are not responsible for the privacy practices, content, or security of external websites. We recommend reviewing the privacy policy of any external website before providing personal information.
            </p>
          </div>

          <div className="policy-section" id="changes">
            <h3>{sections[12].icon} Changes to This Privacy Policy</h3>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our business, website, technology, or applicable legal requirements. Any updated version will be published on this page with a revised “Last Updated” date.
            </p>
          </div>

          <div className="policy-section" id="contact" style={{ background: "#f8fafc", borderRadius: "12px", padding: "30px", border: "1px solid #e2e8f0" }}>
            <h3 style={{ border: "none", padding: "0" }}>{sections[13].icon} Contact Us</h3>
            <p style={{ margin: "0 0 10px 0", color: "#0f172a", fontWeight: "700" }}>
              Sree Raghavendra Hardware, Plywoods & Doors
            </p>
            <p style={{ margin: "0 0 8px 0" }}>
              <strong>Contact Person:</strong> Koushik Mattapalli
            </p>
            <p style={{ margin: "0 0 8px 0" }}>
              <strong>Phone:</strong> <a href="tel:8374771149" style={{ color: brandColor, textDecoration: "none", fontWeight: "600" }}>83747 71149</a>
            </p>
            <p style={{ margin: "0 0 8px 0" }}>
              <strong>Address:</strong> #11-35, Near Vaartha Paper, Kataari Nilayam, Morampudi, Rajamahendravaram, Andhra Pradesh.
            </p>
            <p style={{ margin: "0" }}>
              <strong>Website:</strong> <a href="http://www.raghavendradoorsandhardware.in" target="_blank" rel="noopener noreferrer" style={{ color: brandColor, textDecoration: "none", fontWeight: "600" }}>www.raghavendradoorsandhardware.in</a>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default PrivacyPolicy;